import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setTopics } from '../store/dashboardSlice'
import { useNotification } from './global_noification_component'
import GlobalThrobberComponent from './global_throbber_component'
import { marked } from 'marked'
import twemoji from 'twemoji'
import { publishPanel, refreshPanel } from '../util/internal_sign'

const CHEVRON_RIGHT = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIj48cG9seWxpbmUgcG9pbnRzPSI4IDQgMTQgMTAgOCAxNiIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjRjY2NDM1IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg=='
const CHEVRON_DOWN = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIj48cG9seWxpbmUgcG9pbnRzPSI0IDggMTAgMTQgMTYgOCIgc3Ryb2tlPSIjRjY2NDM1IiBmaWxsPSJub25lIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg=='
const API_BASE = process.env.REACT_APP_API_BASE
const API_VERSION = process.env.REACT_APP_API_ENVIRONMENT
const PANEL_TOPIC_ID = 'TOPIC#Panel'

function DiscordSelect({ options, placeholder = 'Select…' }) {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState(null)

  const toggleOpen = () => setOpen(o => !o)
  const handleSelect = opt => {
    setSelected(opt)
    setOpen(false)
  }

  useEffect(() => {
    const handler = e => {
      if (!e.target.closest('.discord-select-wrapper')) setOpen(false)
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])

  const parseLabel = text => ({ __html: twemoji.parse(text, { folder: 'svg', ext: '.svg' }) })

  return (
    <div
      className={`discord-select-wrapper${open ? ' open' : ''}`}
      onClick={e => {
        e.stopPropagation()
        toggleOpen()
      }}
    >
      <div className="discord-select-display">
        {selected?.emoji && <span className="option-icon">{selected.emoji}</span>}
        <span
          className="option-label"
          dangerouslySetInnerHTML={parseLabel(selected ? selected.label : placeholder)}
        />
      </div>
      {open && (
        <ul className="discord-select-options">
          {options.map((o, i) => (
            <li
              key={i}
              className={`discord-select-option${selected?.value === o.value ? ' selected' : ''}`}
              onClick={e => {
                e.stopPropagation()
                handleSelect(o)
              }}
            >
              {o.emoji && <span className="option-icon">{o.emoji}</span>}
              <span
                className="option-label"
                dangerouslySetInnerHTML={parseLabel(o.label)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function PanelControls({ notify, onPublish, highlightPublish, hasUnsavedChanges, setIsApiLoading }) {
  const [busy, setBusy] = useState(null)

  const publish = async () => {
    const ch = '1383956362362617897'
    setBusy('publish')
    if (setIsApiLoading) setIsApiLoading(true)
    try {
      await publishPanel(ch)
      notify('success', 'Panel published')
      if (onPublish) onPublish()
    } catch (e) {
      notify('error', e.message || 'Publish failed')
    } finally {
      setBusy(null)
      if (setIsApiLoading) setIsApiLoading(false)
    }
  }

  const refresh = async () => {
    const ch = '1383956362362617897'
    setBusy('refresh')
    if (setIsApiLoading) setIsApiLoading(true)
    try {
      await refreshPanel(ch)
      notify('success', 'Panel refreshed')
    } catch (e) {
      notify('error', e.message || 'Refresh failed')
    } finally {
      setBusy(null)
      if (setIsApiLoading) setIsApiLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
      <button
        className="secondary-btn"
        onClick={refresh}
        disabled={busy !== null}
      >
        {busy === 'refresh' ? 'Refreshing…' : 'Refresh Panel'}
      </button>
      <button
        className={`primary-btn${highlightPublish ? ' onboarding-highlight' : ''}`}
        onClick={publish}
        disabled={busy !== null || hasUnsavedChanges}
        title={hasUnsavedChanges ? 'You have unsaved changes that must be saved prior to publishing.' : ''}
      >
        {busy === 'publish' ? 'Publishing…' : 'Publish Panel'}
      </button>
    </div>
  )
}

export default function KnowledgeBaseComponent({ isKbNavbarMode, kbPanelWidth, startKbResize, isMobile }) {
  const topics = useSelector(state => state.dashboard.topics)
  const user = useSelector(state => state.dashboard.user)
  const permissions = useSelector(state => state.dashboard.permissions)
  const dispatch = useDispatch()

  const [editForm, setEditForm] = useState(null)
  const [creating, setCreating] = useState(false)
  const canWrite = permissions?.write && permissions?.knowledge_base
  const canEdit = permissions?.edit && permissions?.knowledge_base
  const canDelete = permissions?.delete && permissions?.knowledge_base
  const isCreator = editForm ? String(editForm.createdBy || '') === String(user?.id || '') : false
  const readOnly = creating ? false : !(canEdit || isCreator)
  const canSave = creating ? canWrite : (canEdit || isCreator)
  const [isApiLoading, setIsApiLoading] = useState(false)
  const [expandedTopics, setExpandedTopics] = useState({})
  const [selectedTopicId, setSelectedTopicId] = useState(null)
  const notify = useNotification()

  const [searchQuery, setSearchQuery] = useState('')
  const [filteredTopics, setFilteredTopics] = useState([])
  const [focusedBranch, setFocusedBranch] = useState(null)
  const [breadcrumbs, setBreadcrumbs] = useState([])
  const [treeViewMode, setTreeViewMode] = useState('full')

  const [newOptionLabel, setNewOptionLabel] = useState('')
  const [newOptionValue, setNewOptionValue] = useState('')
  const [editingIndex, setEditingIndex] = useState(-1)
  const [originalForm, setOriginalForm] = useState(null)
  const [newOptionEmoji, setNewOptionEmoji] = useState('')
  const [newOptionTarget, setNewOptionTarget] = useState('')
  // const [creating, setCreating] = useState(false) // now declared above
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const DRAFT_KEY = "kb_topic_draft"
  const TOPIC_CACHE_KEY = "kb_topic_cache"
  const CACHE_TTL_MS = 5 * 60 * 1000
  const [lastFetched, setLastFetched] = useState(() => {
    const t = Number(localStorage.getItem('kb_last_fetched') || 0)
    return isNaN(t) ? 0 : t
  })
  const [topicCache, setTopicCache] = useState(() => {
    try {
      const data = localStorage.getItem(TOPIC_CACHE_KEY)
      return data ? JSON.parse(data) : []
    } catch {
      return []
    }
  })

  const newBtnRef = useRef(null)
  const titleRef = useRef(null)
  const messageRef = useRef(null)
  const saveBtnRef = useRef(null)
  const breadcrumbRef = useRef(null)
  const searchRef = useRef(null)

  const topicListRef = useRef(null)
  const [topicHeight] = useState(40)
  const [containerHeight, setContainerHeight] = useState(600)
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 })

  useEffect(() => {
    const updateHeight = () => {
      if (topicListRef.current) {
        const rect = topicListRef.current.getBoundingClientRect()
        setContainerHeight(Math.min(rect.height, window.innerHeight * 0.6))
      }
    }
    updateHeight()
    window.addEventListener('resize', updateHeight)
    return () => window.removeEventListener('resize', updateHeight)
  }, [])

  const visibleItemsCount = Math.ceil(containerHeight / topicHeight) + 2

  const formChanged = useMemo(() => {
    if (!editForm) return false
    if (creating) return true
    if (!originalForm) return false
    if (editForm.title !== originalForm.title) return true
    if ((editForm.embedTitle || '') !== (originalForm.embedTitle || '')) return true
    if ((editForm.embedDescription || '') !== (originalForm.embedDescription || '')) return true
    if ((editForm.parentId || '') !== (originalForm.parentId || '')) return true
    if ((editForm.nodeType || 'topic') !== (originalForm.nodeType || 'topic')) return true
    if (!!editForm.hasBackButton !== !!originalForm.hasBackButton) return true
    if ((editForm.embedColor || '') !== (originalForm.embedColor || '')) return true
    if ((editForm.plainMessage || '') !== (originalForm.plainMessage || '')) return true
    const optsA = editForm.dropdownOptions || []
    const optsB = originalForm.dropdownOptions || []
    if (optsA.length !== optsB.length) return true
    for (let i = 0; i < optsA.length; i++) {
      const a = optsA[i] || {}
      const b = optsB[i] || {}
      if ((a.emoji || '') !== (b.emoji || '')) return true
      if ((a.label || '') !== (b.label || '')) return true
      if ((a.value || '') !== (b.value || '')) return true
      if ((a.targetId || '') !== (b.targetId || '')) return true
    }
    return false
  }, [editForm, originalForm, creating])

  const getTopicId = t => String(t.topic_id ?? t.id ?? t.PK)
  const normalize = s => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '')

  const blockIfEditing = () => {
    if (!editForm) return false
    if (formChanged) {
      notify('error', 'Save or cancel your edits first.')
      return true
    }
    setEditForm(null)
    setSelectedTopicId(null)
    setOriginalForm(null)
    setCreating(false)
    return false
  }

  const flattenTopics = nodes => {
    const out = []
    const walk = (items, path = []) => {
      items.forEach(n => {
        const currentPath = [...path, n.title]
        out.push({
          id: getTopicId(n),
          title: n.title,
          path: currentPath,
          depth: path.length,
          node: n
        })
        if (n.children) walk(n.children, currentPath)
      })
    }
    walk(nodes)
    return out
  }

  const updateTopicCache = list => {
    console.log('Updating topic cache with:', list)
    const flat = flattenTopics(list)
    console.log('Flattened topics:', flat)
    setTopicCache(flat)
    setFilteredTopics(flat)
    try {
      localStorage.setItem(TOPIC_CACHE_KEY, JSON.stringify(flat))
    } catch {}
  }

  const parseOptions = val => {
    if (Array.isArray(val)) return val
    if (typeof val === 'string') {
      try {
        const arr = JSON.parse(val)
        return Array.isArray(arr) ? arr : []
      } catch {
        return []
      }
    }
    return []
  }

  const buildTopicTree = useCallback(list => {
    const map = {}

    // First pass: create all nodes
    list.forEach(t => {
      const id = String(getTopicId(t))
      const node = { ...t, dropdownOptions: parseOptions(t.dropdownOptions ?? t.dropdown_options), children: [] }
      map[id] = node
      // Create additional mappings for different ID formats
      if (t.PK && String(t.PK) !== id) map[String(t.PK)] = node
      if (t.topic_id && String(t.topic_id) !== id) map[String(t.topic_id)] = node
      if (t.id && String(t.id) !== id) map[String(t.id)] = node
    })

    const roots = []

    // Second pass: build parent-child relationships
    list.forEach(t => {
      const id = String(getTopicId(t))
      const node = map[id]

      // Try different parent ID field names
      const parentKey = t.parentId ?? t.parent_id ?? t.parentTopicId ?? t.parent_topic_id ?? t.parentid

      console.log(`Topic ${t.title} (${id}): parentKey=${parentKey}, nodeType=${t.nodeType}`)

      // Try to find parent by full ID or by extracting ID from topic_id
      let parentNode = null
      if (parentKey != null && parentKey !== '') {
        // First try direct lookup
        parentNode = map[String(parentKey)]

        // If not found, try to find parent by checking if parentKey matches any topic's ID portion
        if (!parentNode) {
          for (const [key, topic] of Object.entries(map)) {
            // Extract ID from topic_id (e.g., "TOPIC#Panel" -> "Panel", "SUBTOPIC#abc-123" -> "abc-123")
            const topicIdParts = topic.topic_id?.split('#')
            const topicShortId = topicIdParts?.[1] || topic.topic_id

            if (topicShortId === parentKey || key.includes(parentKey)) {
              parentNode = topic
              break
            }
          }
        }
      }

      if (parentNode) {
        // Has valid parent - add as child
        parentNode.children.push(node)
        console.log(`  -> Added as child of ${parentNode.title}`)
      } else {
        // Check if this is the main panel topic
        if (id === PANEL_TOPIC_ID || t.nodeType === 'TOPIC' || t.nodeType === 'topic') {
          // Main panel topic or root topic - add as root
          roots.push(node)
          console.log(`  -> Added as root topic (main panel or root)`)
        } else {
          // This might be an orphaned subtopic - check if main panel exists
          const mainPanel = map[PANEL_TOPIC_ID]
          if (mainPanel) {
            mainPanel.children.push(node)
            console.log(`  -> Added orphaned topic as child of main panel`)
          } else {
            // No main panel found, add as root
            roots.push(node)
            console.log(`  -> Added as root topic (no main panel found)`)
          }
        }
      }
    })

    console.log('Built tree structure:', roots)
    return roots
  }, [])

  const fetchTopics = useCallback(async () => {
    if (Date.now() - lastFetched < CACHE_TTL_MS) {
      return
    }
    setIsApiLoading(true)
    try {
      const res = await fetch(`${API_BASE}/${API_VERSION}/private/knowledge-base/topics`, {
        method: 'GET',
        credentials: 'include'
      })
      if (!res.ok) throw new Error('Failed to load topics')
      const data = await res.json()
      const structured = buildTopicTree(data.topics || [])
      console.log('Fetched topics:', data.topics)
      console.log('Structured tree:', structured)
      setLastFetched(Date.now())
      localStorage.setItem('kb_last_fetched', String(Date.now()))
      dispatch(setTopics(structured))
      updateTopicCache(structured)
    } catch (error) {
      console.error('Failed to fetch topics:', error)
      notify('error', 'Unable to fetch topics')
    } finally {
      setIsApiLoading(false)
    }
  }, [API_BASE, API_VERSION, buildTopicTree, notify, dispatch, updateTopicCache, lastFetched])

  const isDuplicateValue = (value, ignoreIndex = -1) =>
    editForm?.dropdownOptions?.some((opt, idx) =>
      idx !== ignoreIndex && (opt.value || '').trim().toLowerCase() === (value || '').trim().toLowerCase()
    )

  const handleAddOption = () => {
    if (readOnly) {
      notify('error', 'You do not have permission to edit options');
      return;
    }
    const label = newOptionLabel.trim()
    const val = newOptionValue.trim()
    const target = newOptionTarget.trim()
    if (!label || !val) {
      notify('error', 'Please enter both label and value for the option.')
      return
    }
    if (editForm.dropdownOptions?.length >= 25) {
      notify('error', 'Maximum of 25 options allowed.')
      return
    }
    if (isDuplicateValue(val)) {
      notify('error', 'Each option must have a unique value.')
      return
    }
    setEditForm({
      ...editForm,
      dropdownOptions: [...(editForm.dropdownOptions || []), { emoji: newOptionEmoji, label, value: val, targetId: target }]
    })
    setNewOptionLabel('')
    setNewOptionValue('')
    setNewOptionEmoji('')
    setNewOptionTarget('')
  }

  const handleRemoveOption = index => {
    if (readOnly) return;
    const updated = [...(editForm.dropdownOptions || [])]
    updated.splice(index, 1)
    setEditForm({ ...editForm, dropdownOptions: updated })
  }

  const handleOptionChange = (index, field, value) => {
    if (readOnly) return;
    if (editingIndex !== index) setEditingIndex(index)
    const updated = [...(editForm.dropdownOptions || [])]
    updated[index] = { ...updated[index], [field]: value }
    setEditForm({ ...editForm, dropdownOptions: updated })
  }

  const updateTopicField = (id, field, value) => {
    const modify = nodes => nodes.map(n => {
      if (getTopicId(n) === id) return { ...n, [field]: value }
      if (n.children) return { ...n, children: modify(n.children) }
      return n
    })
    const updated = modify(topics)
    updateTopicCache(updated)
    dispatch(setTopics(updated))
  }

  const removeTopicFromTree = (nodes, id) => {
    let removed = null
    const walk = items => items.reduce((acc, n) => {
      if (getTopicId(n) === id) {
        removed = n
        return acc
      }
      if (n.children) {
        const children = walk(n.children)
        acc.push(children !== n.children ? { ...n, children } : n)
      } else {
        acc.push(n)
      }
      return acc
    }, [])
    return { list: walk(nodes), node: removed }
  }

  const insertTopicIntoTree = (nodes, node, parentId) => {
    if (!parentId) return [...nodes, node]
    return nodes.map(n => {
      if (getTopicId(n) === String(parentId)) {
        return { ...n, children: [...(n.children || []), node] }
      }
      if (n.children) return { ...n, children: insertTopicIntoTree(n.children, node, parentId) }
      return n
    })
  }

  const moveTopicInTree = (nodes, id, parentId) => {
    const { list, node } = removeTopicFromTree(nodes, id)
    if (!node) return nodes
    return insertTopicIntoTree(list, node, parentId)
  }

  const moveTopic = (id, parentId) => {
    const target = parentId || (id !== PANEL_TOPIC_ID ? PANEL_TOPIC_ID : '')
    if (String(id) === String(target)) return
    if (target && !topicCache.some(t => String(t.id) === String(target))) return
    const updated = moveTopicInTree(topics, id, target)
    updateTopicCache(updated)
    dispatch(setTopics(updated))
    if (target) setExpandedTopics(e => ({ ...e, [target]: true }))
  }

  const updateTopicNodeInTree = (list, node) => {
    const id = getTopicId(node)
    const modify = items => items.map(n => {
      if (getTopicId(n) === id) return { ...node, children: n.children }
      if (n.children) return { ...n, children: modify(n.children) }
      return n
    })
    return modify(list)
  }

  const getAllSubtopicIds = (topic) => {
    const ids = [getTopicId(topic)]
    if (topic.children && topic.children.length > 0) {
      topic.children.forEach(child => {
        ids.push(...getAllSubtopicIds(child))
      })
    }
    return ids
  }

  const findTopicById = (nodes, targetId) => {
    for (const node of nodes) {
      if (getTopicId(node) === targetId) return node
      if (node.children) {
        const found = findTopicById(node.children, targetId)
        if (found) return found
      }
    }
    return null
  }

  const deleteTopic = async id => {
    if (!canDelete) {
      notify('error', 'You do not have permission to delete topics')
      setShowDeleteModal(false)
      return
    }
    if (String(id) === PANEL_TOPIC_ID) {
      notify('error', 'Main panel message cannot be deleted')
      setShowDeleteModal(false)
      return
    }

    setIsApiLoading(true)
    try {
      // Find the topic and get all its subtopic IDs
      const topicToDelete = findTopicById(topics, id)
      if (!topicToDelete) {
        throw new Error('Topic not found')
      }

      const allTopicIds = getAllSubtopicIds(topicToDelete)

      // Normalize topic_id and subtopic_ids to not include prefix
      const mainId = id.includes('#') ? id.split('#')[1] : id;
      const subIds = allTopicIds
        .filter(topicId => topicId !== id)
        .map(tid => (tid.includes('#') ? tid.split('#')[1] : tid));

      // Send delete request with all topic IDs (parent + subtopics)
      const res = await fetch(`${API_BASE}/${API_VERSION}/private/knowledge-base/topics`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic_id: mainId,
          cascade_delete: true,
          subtopic_ids: subIds,
        })
      })

      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.error || 'Failed to delete topic')
      }

      const { list } = removeTopicFromTree(topics, id)
      updateTopicCache(list)
      dispatch(setTopics(list))

      const subtopicCount = allTopicIds.length - 1
      const message = subtopicCount > 0
        ? `Topic and ${subtopicCount} subtopic${subtopicCount !== 1 ? 's' : ''} deleted`
        : 'Topic deleted'
      notify('success', message)
    } catch (err) {
      notify('error', err.message)
    } finally {
      setIsApiLoading(false)
    }
    setShowDeleteModal(false)
    setEditForm(null)
    setSelectedTopicId(null)
    setOriginalForm(null)
  }

  useEffect(() => {
    fetchTopics()
  }, [])

  useEffect(() => {
    const revalidate = () => {
      if (Date.now() - Number(localStorage.getItem('kb_last_fetched') || 0) > CACHE_TTL_MS) {
        fetchTopics()
      }
    }
    window.addEventListener('focus', revalidate)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) revalidate()
    })
    return () => {
      window.removeEventListener('focus', revalidate)
      document.removeEventListener('visibilitychange', revalidate)
    }
  }, [fetchTopics])

  useEffect(() => {
    if (topics.length > 0) {
      updateTopicCache(topics)
      // Auto-expand first level topics for better initial view
      const firstLevelExpansions = {}
      topics.forEach(topic => {
        if (topic.children && topic.children.length > 0) {
          firstLevelExpansions[getTopicId(topic)] = true
        }
      })
      setExpandedTopics(prev => ({ ...prev, ...firstLevelExpansions }))
    }
  }, [topics])

  const selectTopic = topic => {
    if (blockIfEditing()) return
    const id = getTopicId(topic)

    // If clicking on already selected topic, unselect it
    if (selectedTopicId === id) {
      setSelectedTopicId(null)
      setEditForm(null)
      setOriginalForm(null)
      return
    }

    const nodeType = (topic.nodeType || 'topic').toLowerCase()
    const raw = topic.dropdownOptions ?? topic.dropdown_options ?? []
    const opts = parseOptions(raw)
    const messageText = topic.plainMessage ?? topic.plain_message ?? topic.message ?? ''
    setSelectedTopicId(id)
    setEditForm({
      ...topic,
      nodeType,
      dropdownOptions: opts,
      hasBackButton: nodeType === 'topic' ? false : !!topic.hasBackButton,
      embedColor: topic.embedColor || '#F66435',
      plainMessage: messageText,
    })
    setOriginalForm({
      ...topic,
      nodeType,
      dropdownOptions: opts,
      embedColor: topic.embedColor || '#F66435',
      plainMessage: messageText,
    })
    setNewOptionLabel('')
    setNewOptionValue('')
    setNewOptionTarget('')
    setEditingIndex(-1)
  }

  const toggleTopicExpansion = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }))
  }

  const searchTopics = useCallback(query => {
    const q = query.trim()
    setSearchQuery(q)
    setTreeViewMode(q ? 'search' : 'full')
    if (!q) {
      return
    }
    const qNorm = normalize(q)
    const tokens = q.toLowerCase().split(/\s+/).filter(Boolean).map(normalize)
    const matched = topicCache.filter(topic => {
      const fields = [topic.title, ...topic.path]
      return fields.some(f => {
        const fn = normalize(f)
        if (fn.includes(qNorm)) return true
        return tokens.every(t => fn.includes(t))
      })
    })
    setFilteredTopics(matched)
    const expandIds = new Set()
    matched.forEach(topic => {
      let current = topic.node
      while (current && current.parentId) {
        expandIds.add(current.parentId)
        const parent = topicCache.find(t => getTopicId(t.node) === current.parentId)
        current = parent ? parent.node : null
      }
    })
    setExpandedTopics(prev => {
      const out = { ...prev }
      expandIds.forEach(id => {
        out[id] = true
      })
      return out
    })
  }, [topicCache])

  const focusOnBranch = useCallback((topicId) => {
    const topic = topicCache.find(t => getTopicId(t.node) === topicId)
    if (!topic) return

    setFocusedBranch(topicId)
    setTreeViewMode('focused')

    const crumbs = []
    let current = topic
    while (current) {
      crumbs.unshift({
        id: getTopicId(current.node),
        title: current.title
      })
      if (current.node.parentId) {
        current = topicCache.find(t => getTopicId(t.node) === current.node.parentId)
      } else {
        break
      }
    }
    setBreadcrumbs(crumbs)

    setExpandedTopics(prev => ({ ...prev, [topicId]: true }))
  }, [topicCache])

  const resetTreeView = () => {
    setTreeViewMode('full')
    setFocusedBranch(null)
    setBreadcrumbs([])
    setSearchQuery('')
    setFilteredTopics(topicCache)
  }

  const findTopicInTree = (nodes, targetId) => {
    for (const node of nodes) {
      if (getTopicId(node) === targetId) return node
      if (node.children) {
        const found = findTopicInTree(node.children, targetId)
        if (found) return found
      }
    }
    return null
  }

  const getVisibleTopics = () => {
    switch (treeViewMode) {
      case 'search':
        return buildTreeFromFlat(filteredTopics)
      case 'focused':
        if (!focusedBranch) return topics
        const focusedTopic = topics.find(t => getTopicId(t) === focusedBranch) ||
          findTopicInTree(topics, focusedBranch)
        return focusedTopic ? [focusedTopic] : topics
      default:
        console.log('Getting visible topics:', topics)
        console.log('Topics structure detailed:', topics.map(t => ({
          id: getTopicId(t),
          title: t.title,
          SK: t.SK,
          parentId: t.parentId,
          children: t.children?.length || 0,
          childrenTitles: t.children?.map(c => c.title) || []
        })))
        return topics
    }
  }

  const buildTreeFromFlat = (flatList) => {
    const nodeMap = new Map()
    const roots = []

    flatList.forEach(item => {
      nodeMap.set(getTopicId(item.node), { ...item.node, children: [] })
    })

    flatList.forEach(item => {
      const node = nodeMap.get(getTopicId(item.node))
      if (item.node.parentId && nodeMap.has(item.node.parentId)) {
        nodeMap.get(item.node.parentId).children.push(node)
      } else {
        roots.push(node)
      }
    })

    return roots
  }

  const flattenVisible = useCallback((nodes, depth = 0) => {
    const out = []
    nodes.forEach(n => {
      out.push({ node: n, depth })
      if (expandedTopics[getTopicId(n)] && n.children) {
        out.push(...flattenVisible(n.children, depth + 1))
      }
    })
    return out
  }, [expandedTopics])

  const allVisibleTopics = useMemo(
    () => flattenVisible(getVisibleTopics()),
    [topics, filteredTopics, expandedTopics, treeViewMode, focusedBranch]
  )


  const handleTopicScroll = useCallback(
    e => {
      if (allVisibleTopics.length <= 200) return
      const newStart = Math.floor(e.target.scrollTop / topicHeight)
      if (newStart !== visibleRange.start) {
        setVisibleRange({ start: newStart, end: newStart + visibleItemsCount })
      }
    },
    [allVisibleTopics.length, topicHeight, visibleItemsCount, visibleRange.start]
  )

  const startNewTopic = () => {
    if (!canWrite) return;
    if (!canWrite) {
      notify('error', 'You do not have permission to create topics')
      return
    }
    if (blockIfEditing()) return
    const parent = selectedTopicId ? String(selectedTopicId) : PANEL_TOPIC_ID
    const type = parent === PANEL_TOPIC_ID ? "topic" : "subtopic"
    const draftId = `draft-${Date.now()}`
    const draft = {
      title: "",
      plainMessage: "",
      embedTitle: "",
      embedDescription: "",
      parentId: parent,
      dropdownOptions: [],
      hasBackButton: false,
      nodeType: type,
      embedColor: "#F66435",
      PK: draftId,
      children: []
    }
    const insert = nodes => {
      if (!parent) return [...nodes, draft]
      return nodes.map(n => {
        if (getTopicId(n) === parent) return { ...n, children: [...n.children, draft] }
        if (n.children) return { ...n, children: insert(n.children) }
        return n
      })
    }
    dispatch(setTopics(insert(topics)))
    if (parent) setExpandedTopics(e => ({ ...e, [parent]: true }))
    setEditForm(draft)
    setOriginalForm(null)
    setSelectedTopicId(draftId)
    setCreating(true)
    setNewOptionLabel("")
    setNewOptionValue("")
    setNewOptionEmoji("")
    setNewOptionTarget("")
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
    notify("success", "Draft created")
  }

  const cancelEdit = () => {
    if (creating) {
      const id = getTopicId(editForm)
      const remove = nodes => nodes.filter(n => getTopicId(n) !== id).map(n => n.children ? { ...n, children: remove(n.children) } : n)
      const updated = remove(topics)
      updateTopicCache(updated)
      dispatch(setTopics(updated))
      localStorage.removeItem(DRAFT_KEY)
      setCreating(false)
    } else if (originalForm) {
      const id = getTopicId(editForm)
      let updated = moveTopicInTree(topics, id, originalForm.parentId || '')
      updated = updateTopicNodeInTree(updated, originalForm)
      updateTopicCache(updated)
      dispatch(setTopics(updated))
    }
    setEditForm(null)
    setSelectedTopicId(null)
    setOriginalForm(null)
  }

  useEffect(() => {
    if (creating && editForm) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(editForm))
    }
  }, [creating, editForm])

  const renderTopicNode = (topic, depth = 0) => {
    const topicId = getTopicId(topic)
    const hasChildren = topic.children && topic.children.length > 0
    const isExpanded = expandedTopics[topicId]

    return (
      <>
        <div
          className={`kb-topic-header${selectedTopicId === topicId ? ' active' : ''}`}
          onMouseDown={e => e.preventDefault()}
          onClick={e => {
            e.stopPropagation()
            selectTopic(topic)
          }}
        >
          {hasChildren && (
            <div className="topic-icon-container">
              <button
                className="topic-expand-btn"
                onClick={e => {
                  e.stopPropagation()
                  toggleTopicExpansion(topicId)
                }}
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                <img
                  src={isExpanded ? CHEVRON_DOWN : CHEVRON_RIGHT}
                  alt={isExpanded ? 'Collapse' : 'Expand'}
                  width={10}
                  height={10}
                />
              </button>
            </div>
          )}
          <span className="kb-topic-title">{topic.title || 'Draft Topic'}</span>
          <div className="topic-actions">
            {treeViewMode === 'full' && hasChildren && (
              <button
                className="focus-btn"
                onClick={e => {
                  e.stopPropagation()
                  focusOnBranch(topicId)
                }}
                title="Focus on this branch"
              >
                🔍
              </button>
            )}
          </div>
        </div>
        {hasChildren && isExpanded && (
          <ul className="kb-topic-children">
            {topic.children.map(child => (
              <li key={getTopicId(child)} className={`topic-node depth-${depth + 1}`}>
                {renderTopicNode(child, depth + 1)}
              </li>
            ))}
          </ul>
        )}
      </>
    )
  }


  const saveEdits = async () => {
    if (creating) {
      if (!canWrite) {
        notify('error', 'You do not have permission to create topics')
        return
      }
    } else {
      if (!(canEdit || isCreator)) {
        notify('error', 'You do not have permission to save changes')
        return
      }
    }
    if (!(editForm.title || '').trim()) {
      notify("error", "Title is required")
      return
    }
    setIsApiLoading(true)
    if (creating) {
      const payload = {
        title: (editForm.title || '').trim(),
        plainMessage: (editForm.plainMessage || '').trim(),
        embedTitle: (editForm.embedTitle || '').trim(),
        embedDescription: (editForm.embedDescription || '').trim(),
        parentId: editForm.parentId,
        dropdownOptions: editForm.dropdownOptions,
        hasBackButton: editForm.hasBackButton,
        nodeType: editForm.nodeType === "topic" ? "TOPIC" : "SUBTOPIC",
        createdBy: user?.id || ''
      }
      try {
        const res = await fetch(`${API_BASE}/${API_VERSION}/private/knowledge-base/topics`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })
        if (!res.ok) {
          const err = await res.json()
          throw new Error(err.error || "Failed to create topic")
        }
        const { id } = await res.json()
        const newTopic = { ...editForm, PK: id }
        const draftId = getTopicId(editForm)
        const replace = nodes => nodes.map(n => {
          if (getTopicId(n) === draftId) return { ...newTopic, children: n.children }
          if (n.children) return { ...n, children: replace(n.children) }
          return n
        })
        const updated = replace(topics)
        updateTopicCache(updated)
        dispatch(setTopics(updated))
        localStorage.removeItem(DRAFT_KEY)
        notify("success", "Topic created")
        if (user) {
          localStorage.setItem(`last_topic_${user.id}`, String(id))
        }
      } catch (err) {
        notify("error", err.message)
        setIsApiLoading(false)
        return
      }
    } else {
      const id = getTopicId(editForm)
      let sanitizedParent =
        editForm.parentId && String(editForm.parentId) !== id ? editForm.parentId : ''
      if (id !== PANEL_TOPIC_ID && !sanitizedParent) sanitizedParent = PANEL_TOPIC_ID
      const payload = {
        id,
        nodeType: editForm.nodeType === "topic" ? "TOPIC" : "SUBTOPIC",
        title: (editForm.title || '').trim(),
        plainMessage: (editForm.plainMessage || '').trim(),
        embedTitle: (editForm.embedTitle || '').trim(),
        embedDescription: (editForm.embedDescription || '').trim(),
        dropdownOptions: editForm.dropdownOptions,
        hasBackButton: editForm.hasBackButton,
        embedColor: editForm.embedColor,
        parentId: sanitizedParent
      }
      try {
        const res = await fetch(`${API_BASE}/${API_VERSION}/private/knowledge-base/topics`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })
        if (!res.ok) {
          const err = await res.json()
          throw new Error(err.error || "Failed to update topic")
        }
      } catch (err) {
        notify("error", err.message)
        setIsApiLoading(false)
        return
      }
      let updated = moveTopicInTree(topics, id, sanitizedParent)
      updated = updateTopicNodeInTree(updated, editForm)
      updateTopicCache(updated)
      dispatch(setTopics(updated))
      setExpandedTopics(e => sanitizedParent ? { ...e, [sanitizedParent]: true } : e)
      notify("success", "Topic updated")
      if (user) {
        localStorage.setItem(`last_topic_${user.id}`, String(id))
      }
    }
    setEditingIndex(-1)
    setSelectedTopicId(null)
    setEditForm(null)
    setOriginalForm(null)
    setCreating(false)
    setIsApiLoading(false)
  }

  return (
    <>
      {isApiLoading && <GlobalThrobberComponent />}
      {isKbNavbarMode ? (
        <div className="kb-navbar-layout">
          <div className="kb-sidebar-panel" style={{ flexBasis: kbPanelWidth }}>
            <div className="topics-section">
              <div className="topics-header">
                <h3>KB Topics ({topicCache.length})</h3>
                {canWrite && (
                  <button
                    ref={newBtnRef}
                    className="create-topic-btn"
                    onClick={() => startNewTopic()}
                  >
                    + New
                  </button>
                )}
              </div>

              {breadcrumbs.length > 0 && (
                <div
                  className="breadcrumb-nav"
                  ref={breadcrumbRef}
                >
                  <button
                    className="breadcrumb-reset-btn"
                    onClick={resetTreeView}
                    title="Show all topics"
                  >
                    ← All Topics
                  </button>
                  <div className="breadcrumb-path">
                    {breadcrumbs.map((crumb, index) => (
                      <span key={crumb.id} className="breadcrumb-item">
                        {index > 0 && <span className="breadcrumb-separator">›</span>}
                        {index < breadcrumbs.length - 1 ? (
                          <button className="breadcrumb-btn" onClick={() => focusOnBranch(crumb.id)}>
                            {crumb.title}
                          </button>
                        ) : (
                          <span className="breadcrumb-title">{crumb.title}</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="topic-controls">
                <div className="search-box">
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Search topics..."
                    value={searchQuery}
                    onChange={e => searchTopics(e.target.value)}
                    className="topic-search"
                  />
                  {searchQuery && (
                    <button
                      className="clear-search"
                      onClick={() => searchTopics('')}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {searchQuery && (
                  <div className="search-results">
                    {filteredTopics.length > 0 ? (
                      `${filteredTopics.length} result${filteredTopics.length !== 1 ? 's' : ''}`
                    ) : (
                      'No topics found'
                    )}
                  </div>
                )}
              </div>

              <div
                className="kb-topics-wrapper"
                ref={topicListRef}
                onScroll={handleTopicScroll}
                onClick={e => {
                  // Only unselect if clicking directly on the wrapper (not on a topic)
                  if (e.target === e.currentTarget) {
                    if (!blockIfEditing()) {
                      setSelectedTopicId(null)
                      setEditForm(null)
                      setOriginalForm(null)
                    }
                  }
                }}
                style={{ height: `${containerHeight}px`, overflowY: 'auto', position: 'relative' }}
              >
                {getVisibleTopics().length > 0 ? (
                  <ul className="kb-topics-tree">
                    {getVisibleTopics().map(topic => (
                      <li key={getTopicId(topic)} className={`topic-node depth-0`}>
                        {renderTopicNode(topic, 0)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="no-topics">
                    {searchQuery ? 'No topics found' : 'No topics yet'}
                  </div>
                )}
              </div>
            </div>

            <div className="panel-controls">
              <PanelControls
                notify={notify}
                hasUnsavedChanges={formChanged}
                setIsApiLoading={setIsApiLoading}
              />
            </div>

            {!isMobile && (
              <div className="kb-panel-resizer" onMouseDown={startKbResize}></div>
            )}
          </div>

          <div className="kb-content-area">
            {editForm ? (
              <>
                <div className="edit-header">
                  <h2>Edit Topic</h2>
                  <div className="edit-actions">
                    <button className="secondary-btn" onClick={cancelEdit}>
                      Cancel
                    </button>
                    {formChanged && canSave && (
                      <button
                        ref={saveBtnRef}
                        className="primary-btn"
                        onClick={saveEdits}
                        disabled={!canSave}
                      >
                        {creating ? 'Create Topic' : 'Save Changes'}
                      </button>
                    )}
                    {canDelete && (
                      <button className="delete-btn" onClick={() => setShowDeleteModal(true)}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                <div className="edit-preview-container">
                  <form className="modal-form edit-panel">
                    <div className="form-section">
                      <h3>Basic Information</h3>
                      <label>
                        Name
                        <input
                          ref={titleRef}
                          value={editForm.title}
                          onChange={e => {
                            const val = e.target.value
                            setEditForm({ ...editForm, title: val })
                            updateTopicField(getTopicId(editForm), 'title', val)
                          }}
                          placeholder="Enter topic name"
                          disabled={readOnly}
                        />
                      </label>
                      <label>
                        Message (plain text)
                        <textarea
                          ref={messageRef}
                          rows="2"
                          value={editForm.plainMessage || ''}
                          onChange={e => setEditForm({ ...editForm, plainMessage: e.target.value })}
                          placeholder="Enter message"
                          disabled={readOnly}
                        />
                      </label>
                      <label>
                        Embed Title
                        <input
                          value={editForm.embedTitle || ''}
                          onChange={e => setEditForm({ ...editForm, embedTitle: e.target.value })}
                          placeholder="Enter embed title"
                          disabled={readOnly}
                        />
                      </label>
                      <label>
                        Embed Description
                        <textarea
                          rows="4"
                          value={editForm.embedDescription || ''}
                          onChange={e => setEditForm({ ...editForm, embedDescription: e.target.value })}
                          placeholder="Enter embed description"
                          disabled={readOnly}
                        />
                      </label>
                      <div className="embed-settings-box">
                        <label>
                          Embed Color
                          <input
                            type="color"
                            value={editForm.embedColor || '#F66435'}
                            onChange={e => setEditForm({ ...editForm, embedColor: e.target.value })}
                            disabled={readOnly}
                          />
                        </label>
                        {editForm.nodeType !== 'topic' && (
                          <label className="embed-back-checkbox">
                            Back button
                            <input
                              type="checkbox"
                              checked={!!editForm.hasBackButton}
                              onChange={e => setEditForm({ ...editForm, hasBackButton: e.target.checked })}
                              disabled={readOnly}
                            />
                          </label>
                        )}
                      </div>
                      <label>
                        Parent ID
                        <input
                          list="parent-topic-list"
                          value={editForm.parentId || ''}
                          onChange={e => {
                            const v = e.target.value
                            setEditForm({ ...editForm, parentId: v })
                            const id = getTopicId(editForm)
                            if (!v || (topicCache.some(o => String(o.id) === v) && v !== id)) {
                              moveTopic(id, v)
                            }
                          }}
                          placeholder="Select parent topic"
                          disabled={readOnly}
                        />
                        <datalist id="parent-topic-list">
                          {topicCache.map(o => (
                            <option key={o.id} value={o.id}>{o.title}</option>
                          ))}
                        </datalist>
                      </label>
                      <label>
                        Type
                        <select
                          value={editForm.nodeType || 'topic'}
                          onChange={e => {
                            const v = e.target.value.toLowerCase()
                            setEditForm({
                              ...editForm,
                              nodeType: v,
                              hasBackButton: v === 'topic' ? false : editForm.hasBackButton
                            })
                          }}
                          className="select-field"
                          disabled={readOnly}
                        >
                          <option value="topic">Topic</option>
                          <option value="subtopic">Sub Topic</option>
                        </select>
                      </label>
                    </div>

                    <div className="form-section">
                      <h3>Dropdown Options</h3>
                      <div className="options-input">
                        <input
                          className="emoji-field"
                          placeholder="😀"
                          value={newOptionEmoji}
                          onChange={e => setNewOptionEmoji(e.target.value)}
                          maxLength={2}
                        />
                        <div className="option-text-fields">
                          <input
                            className="option-label-field"
                            placeholder="Option label"
                            value={newOptionLabel}
                            onChange={e => setNewOptionLabel(e.target.value)}
                          />
                          <input
                            className="option-value-field"
                            placeholder="Option value"
                            value={newOptionValue}
                            onChange={e => setNewOptionValue(e.target.value)}
                          />
                          <input
                            className="option-target-field"
                            list="option-topic-list"
                            placeholder="Target topic"
                            value={newOptionTarget}
                            onChange={e => setNewOptionTarget(e.target.value)}
                          />
                        </div>
                        <datalist id="option-topic-list">
                          {topicCache.map(o => (
                            <option key={o.id} value={o.id}>{o.title}</option>
                          ))}
                        </datalist>
                      </div>
                      <button
                        type="button"
                        className="add-btn add-option-btn"
                        onClick={handleAddOption}
                        disabled={readOnly || editForm.dropdownOptions?.length >= 25}
                      >
                        Add Option
                      </button>
                      <div className="options-list">
                        {editForm.dropdownOptions?.map((opt, idx) => (
                          <div className="option-row" key={idx}>
                            <input
                              className="emoji-field"
                              value={opt.emoji || ''}
                              onChange={e => handleOptionChange(idx, 'emoji', e.target.value)}
                              disabled={editingIndex !== idx || readOnly}
                              placeholder="😀"
                              maxLength={2}
                            />
                            <input
                              value={opt.label}
                              onChange={e => handleOptionChange(idx, 'label', e.target.value)}
                              disabled={editingIndex !== idx || readOnly}
                              placeholder="Label"
                            />
                            <input
                              value={opt.value}
                              onChange={e => handleOptionChange(idx, 'value', e.target.value)}
                              disabled={editingIndex !== idx || readOnly}
                              placeholder="Value"
                            />
                            <input
                              list="option-topic-list"
                              value={opt.targetId || ''}
                              onChange={e => handleOptionChange(idx, 'targetId', e.target.value)}
                              disabled={editingIndex !== idx || readOnly}
                              placeholder="Target topic"
                            />
                            <div className="option-actions">
                              <button
                                type="button"
                                className="edit-btn"
                                onClick={() => {
                                  if (editingIndex === idx) {
                                    const curr = editForm.dropdownOptions[idx]
                                    if (!(curr.label || '').trim() || !(curr.value || '').trim()) {
                                      notify('error', 'Label and value cannot be empty.')
                                      return
                                    }
                                    if (isDuplicateValue(curr.value, idx)) {
                                      notify('error', 'Each option must have a unique value.')
                                      return
                                    }
                                    setEditingIndex(-1)
                                  } else {
                                    setEditingIndex(idx)
                                  }
                                }}
                                disabled={readOnly}
                              >
                                {editingIndex === idx ? 'Save' : 'Edit'}
                              </button>
                              <button
                                type="button"
                                className="remove-btn"
                                onClick={() => handleRemoveOption(idx)}
                                disabled={readOnly}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </form>
                  <div className="discord-preview">
                    <div className="preview-header">
                      <h3>Discord Preview</h3>
                      <div className="preview-tag">LIVE</div>
                    </div>
                    <div className="discord-message">
                      <img src="https://cdn.ballhead.app/web_assets/laces.png" alt="Laces" className="avatar" />
                      <div className="msg-body">
                        <div className="msg-header">
                          <span className="username">Laces</span>
                          <span className="bot-tag">BOT</span>
                          <span className="timestamp">Today&nbsp;at&nbsp;12:34&nbsp;PM</span>
                        </div>
                        {editForm.plainMessage && (
                          <div
                            className="msg-content"
                            dangerouslySetInnerHTML={{ __html: marked.parseInline(editForm.plainMessage) }}
                          />
                        )}
                        <div className="embed" style={{ borderLeftColor: editForm.embedColor || '#F66435' }}>
                          <div className="embed-accent"></div>
                          <div className="embed-body">
                            <div className="embed-title">{editForm.embedTitle || 'Embed title'}</div>
                            <div
                              className="embed-desc"
                              dangerouslySetInnerHTML={{ __html: marked.parse(editForm.embedDescription || 'Embed description preview') }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {editForm.dropdownOptions?.length > 0 && (
                      <div className="components-row">
                        <DiscordSelect options={editForm.dropdownOptions} />
                      </div>
                    )}
                    {editForm.hasBackButton && (
                      <button type="button" className="back-btn-preview">
                        Back
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="kb-welcome">
                <div className="empty-icon">📚</div>
                <h2>Knowledge Base</h2>
                <p>Select a topic from the navigation panel to begin editing</p>
                {canWrite && (
                  <button className="primary-btn" onClick={() => startNewTopic()}>
                    Create New Topic
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="kb-dashboard-layout">
          <div className="kb-navigation-panel" style={{ flexBasis: kbPanelWidth }}>
            <div className="topics-section">
              <div className="topics-header">
                <h3>KB Topics ({topicCache.length})</h3>
                {canWrite && (
                  <button
                    ref={newBtnRef}
                    className="create-topic-btn"
                    onClick={() => startNewTopic()}
                  >
                    + New
                  </button>
                )}
              </div>

              {breadcrumbs.length > 0 && (
                <div
                  className="breadcrumb-nav"
                  ref={breadcrumbRef}
                >
                  <button
                    className="breadcrumb-reset-btn"
                    onClick={resetTreeView}
                    title="Show all topics"
                  >
                    ← All Topics
                  </button>
                  <div className="breadcrumb-path">
                    {breadcrumbs.map((crumb, index) => (
                      <span key={crumb.id} className="breadcrumb-item">
                        {index > 0 && <span className="breadcrumb-separator">›</span>}
                        {index < breadcrumbs.length - 1 ? (
                          <button className="breadcrumb-btn" onClick={() => focusOnBranch(crumb.id)}>
                            {crumb.title}
                          </button>
                        ) : (
                          <span className="breadcrumb-title">{crumb.title}</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="topic-controls">
                <div className="search-box">
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Search topics..."
                    value={searchQuery}
                    onChange={e => searchTopics(e.target.value)}
                    className="topic-search"
                  />
                  {searchQuery && (
                    <button
                      className="clear-search"
                      onClick={() => searchTopics('')}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {searchQuery && (
                  <div className="search-results">
                    {filteredTopics.length > 0 ? (
                      `${filteredTopics.length} result${filteredTopics.length !== 1 ? 's' : ''}`
                    ) : (
                      'No topics found'
                    )}
                  </div>
                )}
              </div>

              <div
                className="kb-topics-wrapper"
                ref={topicListRef}
                onScroll={handleTopicScroll}
                onClick={e => {
                  // Only unselect if clicking directly on the wrapper (not on a topic)
                  if (e.target === e.currentTarget) {
                    if (!blockIfEditing()) {
                      setSelectedTopicId(null)
                      setEditForm(null)
                      setOriginalForm(null)
                    }
                  }
                }}
                style={{ height: `${containerHeight}px`, overflowY: 'auto', position: 'relative' }}
              >
                {getVisibleTopics().length > 0 ? (
                  <ul className="kb-topics-tree">
                    {getVisibleTopics().map(topic => (
                      <li key={getTopicId(topic)} className={`topic-node depth-0`}>
                        {renderTopicNode(topic, 0)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="no-topics">
                    {searchQuery ? 'No topics found' : 'No topics yet'}
                  </div>
                )}
              </div>
            </div>

            <div className="panel-controls">
              <PanelControls
                notify={notify}
                hasUnsavedChanges={formChanged}
                setIsApiLoading={setIsApiLoading}
              />
            </div>

            {!isMobile && (
              <div className="kb-panel-resizer" onMouseDown={startKbResize}></div>
            )}
          </div>

          <div className="kb-content-panel">
            {editForm ? (
              <>
                <div className="edit-header">
                  <h2>Edit Topic</h2>
                  <div className="edit-actions">
                    <button className="secondary-btn" onClick={cancelEdit}>
                      Cancel
                    </button>
                    {formChanged && canSave && (
                      <button
                        ref={saveBtnRef}
                        className="primary-btn"
                        onClick={saveEdits}
                        disabled={!canSave}
                      >
                        {creating ? 'Create Topic' : 'Save Changes'}
                      </button>
                    )}
                    {canDelete && (
                      <button className="delete-btn" onClick={() => setShowDeleteModal(true)}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                <div className="edit-preview-container">
                  <form className="modal-form edit-panel">
                    <div className="form-section">
                      <h3>Basic Information</h3>
                      <label>
                        Name
                        <input
                          ref={titleRef}
                          value={editForm.title}
                          onChange={e => {
                            const val = e.target.value
                            setEditForm({ ...editForm, title: val })
                            updateTopicField(getTopicId(editForm), 'title', val)
                          }}
                          placeholder="Enter topic name"
                        />
                      </label>
                      <label>
                        Message (plain text)
                        <textarea
                          ref={messageRef}
                          rows="2"
                          value={editForm.plainMessage || ''}
                          onChange={e => setEditForm({ ...editForm, plainMessage: e.target.value })}
                          placeholder="Enter message"
                        />
                      </label>
                      <label>
                        Embed Title
                        <input
                          value={editForm.embedTitle || ''}
                          onChange={e => setEditForm({ ...editForm, embedTitle: e.target.value })}
                          placeholder="Enter embed title"
                        />
                      </label>
                      <label>
                        Embed Description
                        <textarea
                          rows="4"
                          value={editForm.embedDescription || ''}
                          onChange={e => setEditForm({ ...editForm, embedDescription: e.target.value })}
                          placeholder="Enter embed description"
                        />
                      </label>
                      <div className="embed-settings-box">
                        <label>
                          Embed Color
                          <input
                            type="color"
                            value={editForm.embedColor || '#F66435'}
                            onChange={e => setEditForm({ ...editForm, embedColor: e.target.value })}
                          />
                        </label>
                        {editForm.nodeType !== 'topic' && (
                          <label className="embed-back-checkbox">
                            Back button
                            <input
                              type="checkbox"
                              checked={!!editForm.hasBackButton}
                              onChange={e => setEditForm({ ...editForm, hasBackButton: e.target.checked })}
                            />
                          </label>
                        )}
                      </div>
                      <label>
                        Parent ID
                        <input
                          list="parent-topic-list"
                          value={editForm.parentId || ''}
                          onChange={e => {
                            const v = e.target.value
                            setEditForm({ ...editForm, parentId: v })
                            const id = getTopicId(editForm)
                            if (!v || (topicCache.some(o => String(o.id) === v) && v !== id)) {
                              moveTopic(id, v)
                            }
                          }}
                          placeholder="Select parent topic"
                        />
                        <datalist id="parent-topic-list">
                          {topicCache.map(o => (
                            <option key={o.id} value={o.id}>{o.title}</option>
                          ))}
                        </datalist>
                      </label>
                      <label>
                        Type
                        <select
                          value={editForm.nodeType || 'topic'}
                          onChange={e => {
                            const v = e.target.value.toLowerCase()
                            setEditForm({
                              ...editForm,
                              nodeType: v,
                              hasBackButton: v === 'topic' ? false : editForm.hasBackButton
                            })
                          }}
                          className="select-field"
                        >
                          <option value="topic">Topic</option>
                          <option value="subtopic">Sub Topic</option>
                        </select>
                      </label>
                    </div>

                    <div className="form-section">
                      <h3>Dropdown Options</h3>
                      <div className="options-input">
                        <input
                          className="emoji-field"
                          placeholder="😀"
                          value={newOptionEmoji}
                          onChange={e => setNewOptionEmoji(e.target.value)}
                          maxLength={2}
                        />
                        <div className="option-text-fields">
                          <input
                            className="option-label-field"
                            placeholder="Option label"
                            value={newOptionLabel}
                            onChange={e => setNewOptionLabel(e.target.value)}
                          />
                          <input
                            className="option-value-field"
                            placeholder="Option value"
                            value={newOptionValue}
                            onChange={e => setNewOptionValue(e.target.value)}
                          />
                          <input
                            className="option-target-field"
                            list="option-topic-list"
                            placeholder="Target topic"
                            value={newOptionTarget}
                            onChange={e => setNewOptionTarget(e.target.value)}
                          />
                        </div>
                        <datalist id="option-topic-list">
                          {topicCache.map(o => (
                            <option key={o.id} value={o.id}>{o.title}</option>
                          ))}
                        </datalist>
                      </div>
                      <button
                        type="button"
                        className="add-btn add-option-btn"
                        onClick={handleAddOption}
                        disabled={editForm.dropdownOptions?.length >= 25}
                      >
                        Add Option
                      </button>
                      <div className="options-list">
                        {editForm.dropdownOptions?.map((opt, idx) => (
                          <div className="option-row" key={idx}>
                            <input
                              className="emoji-field"
                              value={opt.emoji || ''}
                              onChange={e => handleOptionChange(idx, 'emoji', e.target.value)}
                              disabled={editingIndex !== idx}
                              placeholder="😀"
                              maxLength={2}
                            />
                            <input
                              value={opt.label}
                              onChange={e => handleOptionChange(idx, 'label', e.target.value)}
                              disabled={editingIndex !== idx}
                              placeholder="Label"
                            />
                            <input
                              value={opt.value}
                              onChange={e => handleOptionChange(idx, 'value', e.target.value)}
                              disabled={editingIndex !== idx}
                              placeholder="Value"
                            />
                            <input
                              list="option-topic-list"
                              value={opt.targetId || ''}
                              onChange={e => handleOptionChange(idx, 'targetId', e.target.value)}
                              disabled={editingIndex !== idx}
                              placeholder="Target topic"
                            />
                            <div className="option-actions">
                              <button
                                type="button"
                                className="edit-btn"
                                onClick={() => {
                                  if (editingIndex === idx) {
                                    const curr = editForm.dropdownOptions[idx]
                                    if (!(curr.label || '').trim() || !(curr.value || '').trim()) {
                                      notify('error', 'Label and value cannot be empty.')
                                      return
                                    }
                                    if (isDuplicateValue(curr.value, idx)) {
                                      notify('error', 'Each option must have a unique value.')
                                      return
                                    }
                                    setEditingIndex(-1)
                                  } else {
                                    setEditingIndex(idx)
                                  }
                                }}
                              >
                                {editingIndex === idx ? 'Save' : 'Edit'}
                              </button>
                              <button
                                type="button"
                                className="remove-btn"
                                onClick={() => handleRemoveOption(idx)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </form>
                  <div className="discord-preview">
                    <div className="preview-header">
                      <h3>Discord Preview</h3>
                      <div className="preview-tag">LIVE</div>
                    </div>
                    <div className="discord-message">
                      <img src="https://cdn.ballhead.app/web_assets/laces.png" alt="Laces" className="avatar" />
                      <div className="msg-body">
                        <div className="msg-header">
                          <span className="username">Laces</span>
                          <span className="bot-tag">BOT</span>
                          <span className="timestamp">Today&nbsp;at&nbsp;12:34&nbsp;PM</span>
                        </div>
                        {editForm.plainMessage && (
                          <div
                            className="msg-content"
                            dangerouslySetInnerHTML={{ __html: marked.parseInline(editForm.plainMessage) }}
                          />
                        )}
                        <div className="embed" style={{ borderLeftColor: editForm.embedColor || '#F66435' }}>
                          <div className="embed-accent"></div>
                          <div className="embed-body">
                            <div className="embed-title">{editForm.embedTitle || 'Embed title'}</div>
                            <div
                              className="embed-desc"
                              dangerouslySetInnerHTML={{ __html: marked.parse(editForm.embedDescription || 'Embed description preview') }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {editForm.dropdownOptions?.length > 0 && (
                      <div className="components-row">
                        <DiscordSelect options={editForm.dropdownOptions} />
                      </div>
                    )}
                    {editForm.hasBackButton && (
                      <button type="button" className="back-btn-preview">
                        Back
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="kb-welcome">
                <div className="empty-icon">📚</div>
                <h2>Knowledge Base</h2>
                <p>Select a topic from the navigation panel to begin editing</p>
                {canWrite && (
                  <button className="primary-btn" onClick={() => startNewTopic()}>
                    Create New Topic
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="confirm-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <h4>Confirm Deletion</h4>
            {(() => {
              if (!editForm) return null
              const topicToDelete = findTopicById(topics, getTopicId(editForm))
              if (!topicToDelete) return null
              const allIds = getAllSubtopicIds(topicToDelete)
              const subtopicCount = allIds.length - 1
              
              return subtopicCount > 0 ? (
                <p>
                  Are you sure you want to delete "<strong>{topicToDelete.title}</strong>" and its <strong>{subtopicCount} subtopic{subtopicCount !== 1 ? 's' : ''}</strong>? 
                  <br /><br />
                  This action cannot be undone.
                </p>
              ) : (
                <p>Are you sure you want to delete this topic? This action cannot be undone.</p>
              )
            })()}
            <div className="actions">
              <button className="secondary-btn" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="delete-btn" onClick={() => deleteTopic(getTopicId(editForm))}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}